import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Mail, Phone, CalendarClock, Clock, CheckCircle2, Loader2 } from "lucide-react";
import { MarketingNav, MarketingFooter } from "@/components/TailorWizMarketingHome";
import { supabase } from "@/integrations/supabase/client";

const FAQ = [
  {
    q: "How fast can I expect a response?",
    a: "Most emails get a personal response within a few hours during business hours. Account and billing questions are typically resolved within one business day.",
  },
  {
    q: "Do you offer one-on-one coaching outside of TailorWiz?",
    a: "Yes. Marcus offers Executive Resume Audits and full-service career coaching through Results Driven Resumes and Executive Jobs on Demand. Reach out for current availability.",
  },
  {
    q: "I am a career coach. Can I use TailorWiz for my clients?",
    a: "Yes. We have a team plan built specifically for career coaches, resume writers, and recruiting agencies. Reach out for pricing and white-label options.",
  },
];

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <MarketingNav />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F] text-white">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.16]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 18% 25%, #C9A84C 0, transparent 38%), radial-gradient(circle at 82% 75%, #4A78C2 0, transparent 38%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-6 pt-16 pb-14 sm:pt-20 sm:pb-16 text-center">
          <span className="inline-flex items-center gap-2 text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.18em] text-[#C9A84C] bg-white/5 px-4 py-2 rounded-full border border-[#C9A84C]/40">
            <Sparkles className="w-3.5 h-3.5 text-[#C9A84C]" />
            We Are Here to Help
          </span>
          <h1 className="mt-6 text-[34px] sm:text-[48px] font-extrabold tracking-tight leading-[1.07]">
            Talk to <span className="text-[#C9A84C]">Real Humans.</span>
          </h1>
          <p className="mt-5 text-[16px] sm:text-[18px] leading-[1.6] text-slate-200">
            TailorWiz is built and supported by a small team led personally by
            Marcus Hall. When you reach out, you reach actual humans who know the
            platform inside and out. Most messages get a personal response
            within a few hours.
          </p>
        </div>
      </section>

      {/* CONTACT OPTIONS */}
      <section className="py-14 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              icon: Mail,
              title: "Email Us",
              body: "For account questions, billing, technical support, or general inquiries.",
              detail: "marcus@tailorwiz.com",
              href: "mailto:marcus@tailorwiz.com",
              note: "Typical response: within a few hours.",
            },
            {
              icon: Phone,
              title: "Call or Text",
              body: "Talk to a human. Quick questions, account issues, or feedback you want to share directly.",
              detail: "281-883-6465",
              href: "tel:2818836465",
              note: "Monday to Friday, 9 AM to 6 PM Central.",
            },
            {
              icon: CalendarClock,
              title: "Book a Live Demo",
              body: "See TailorWiz live 1-on-1 — or talk executive resume audits and partnerships.",
              detail: "Pick a time on the calendar",
              href: "https://calendly.com/marcusbhall/tailorwiz-live-demo",
              note: "Booked instantly — no back-and-forth.",
            },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#C9A84C]/50 hover:shadow-lg transition-all flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-[#0B1C3F] flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#C9A84C]" />
                </div>
                <h3 className="mt-4 text-[18px] font-bold text-slate-900">{c.title}</h3>
                <p className="mt-1.5 text-[14px] text-slate-600 leading-[1.5] flex-1">
                  {c.body}
                </p>
                <a
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="mt-3 text-[15px] font-bold text-[#0B1C3F] hover:text-[#C9A84C] transition-colors break-words"
                >
                  {c.detail}
                </a>
                <p className="mt-1 text-[12px] text-slate-400">{c.note}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTACT FORM — wired to the send-contact-message edge function,
          which delivers to marcus@tailorwiz.com and auto-replies. */}
      <section className="py-14 sm:py-16 bg-slate-50">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-center text-[26px] sm:text-[34px] font-extrabold tracking-tight text-slate-900">
            Send Us a Message
          </h2>
          <ContactForm />
        </div>
      </section>

      {/* ABOUT THE FOUNDER */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <span className="text-[12px] font-extrabold tracking-[0.15em] uppercase text-[#C9A84C]">
            Behind the Platform
          </span>
          <h2 className="mt-2 text-[28px] sm:text-[36px] font-extrabold tracking-tight text-slate-900">
            Built by Marcus Hall
          </h2>
          <div className="mt-4 space-y-4 text-[15px] sm:text-[16px] leading-[1.65] text-slate-600">
            <p>
              Marcus Hall is a faith-based executive career strategist with 14+
              years of experience personally writing resumes and coaching 6 and
              7 figure professionals into the highest paying roles of their careers.
            </p>
            <p>
              He is the world's #1 faith-based career creator on LinkedIn with
              90K+ engaged followers and 2 million plus monthly reach. He is the
              founder of Results Driven Resumes, Executive Jobs on Demand, and
              the Jiodee! faith-based book and inspiration platform.
            </p>
            <p>
              TailorWiz is the platform he built to put everything he knows about
              modern executive job search into the hands of every professional
              ready to stop being filtered out by the system.
            </p>
          </div>
        </div>
      </section>

      {/* QUICK FAQ */}
      <section className="py-14 sm:py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-center text-[24px] sm:text-[32px] font-extrabold tracking-tight text-slate-900">
            Quick Answers
          </h2>
          <div className="mt-7 space-y-4">
            {FAQ.map((item) => (
              <div key={item.q} className="rounded-xl bg-white border border-slate-200 p-5">
                <h3 className="text-[15px] font-bold text-slate-900">{item.q}</h3>
                <p className="mt-1.5 text-[14px] leading-[1.55] text-slate-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-b from-[#0B1C3F] to-[#0E2350] text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-[30px] sm:text-[40px] font-extrabold tracking-tight">
            Ready When You Are.
          </h2>
          <p className="mt-4 text-[16px] text-slate-200">
            Skip the wait — get started now and see TailorWiz in action
            right now.
          </p>
          <div className="mt-7">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#C9A84C] text-[#0B1C3F] text-[16px] font-bold shadow-lg shadow-[#C9A84C]/30 hover:bg-[#E5C36A] transition-all"
            >
              Try It Out FREE Now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("General Question");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError("Please fill in your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      const { error: fnError } = await supabase.functions.invoke("send-contact-message", {
        body: { name: name.trim(), email: email.trim(), subject, message: message.trim() },
      });
      if (fnError) throw fnError;
      setSent(true);
    } catch {
      setError("Couldn't send your message. Please email us directly at marcus@tailorwiz.com.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="mt-8 rounded-2xl bg-white border border-emerald-200 p-10 text-center">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
        <p className="text-[20px] font-extrabold text-slate-900">Message sent!</p>
        <p className="mt-1 text-[14px] text-slate-500">
          We'll reply to <span className="font-semibold text-slate-700">{email}</span> — usually within a few hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-8 rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] focus:border-[#C9A84C] focus:ring-1 focus:ring-[#C9A84C] outline-none"
            placeholder="Your name"
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] focus:border-[#C9A84C] focus:ring-1 focus:ring-[#C9A84C] outline-none"
            placeholder="yourname@email.com"
          />
        </Field>
      </div>
      <Field label="Subject">
        <select
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] focus:border-[#C9A84C] focus:ring-1 focus:ring-[#C9A84C] outline-none bg-white"
        >
          {[
            "General Question",
            "Account or Billing",
            "Technical Support",
            "Partnership or Team Pricing",
            "Feedback or Feature Request",
            "Press or Media Inquiry",
          ].map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      </Field>
      <Field label="Message">
        <textarea
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] focus:border-[#C9A84C] focus:ring-1 focus:ring-[#C9A84C] outline-none resize-none"
          placeholder="How can we help?"
        />
      </Field>
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">{error}</div>
      )}
      <button
        type="submit"
        disabled={sending}
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C9A84C] text-[#0B1C3F] text-[15px] font-bold hover:bg-[#E5C36A] disabled:opacity-50 transition-colors"
      >
        {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send Message <ArrowRight className="w-4 h-4" /></>}
      </button>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[13px] font-semibold text-slate-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

// keep Clock import referenced
void Clock;
